/**
 * Per-metric unit registry for soil analyses.
 *
 * The same indicator is reported in different units by region (cations in
 * mmolc/dm³ under IAC vs cmolc/dm³ under CFSEMG; texture and organic matter in
 * g/kg vs %; K in both mg/dm³ *and* cmolc/dm³ on the very same report). So each
 * captured metric is stored as a `{ value, unit }` pair, the `unit` validated
 * against this registry on write (a plain string — *not* a DB enum — so new
 * units land in code without a migration), and the interpretation engine
 * normalises every reading to the metric's `canonicalUnit` before any math.
 *
 * Units belong to a small set of **families** (cations, phosphorus, texture, …);
 * the registry maps each metric to its family, and the family declares the
 * allowed unit set, the default unit, the canonical unit, and the factor that
 * converts each allowed unit to the canonical one.
 */
// ============================================
// UNIT FAMILIES (allowed sets as `const` → derived unions)
// ============================================
export const PHOSPHORUS_UNITS = ["mg/dm³", "mg/kg", "ppm"];
export const POTASSIUM_UNITS = ["mg/dm³", "cmolc/dm³", "mmolc/dm³"];
export const SODIUM_UNITS = ["mg/dm³", "cmolc/dm³", "mmolc/dm³"];
/** Ca, Mg, Al, H+Al and the derived sums (SB, t, T). */
export const CATION_UNITS = ["cmolc/dm³", "mmolc/dm³"];
export const SULFUR_UNITS = ["mg/dm³", "mg/kg", "ppm"];
export const MICRONUTRIENT_UNITS = ["mg/dm³", "mg/kg", "ppm"];
/** Organic matter and organic carbon. */
export const ORGANIC_UNITS = ["g/kg", "dag/kg", "g/dm³"];
/** Clay, silt, sand. */
export const TEXTURE_UNITS = ["g/kg", "%"];
/** V% (base saturation) and m% (aluminium saturation). */
export const PERCENT_UNITS = ["%"];
// mg/dm³ → cmolc/dm³: a cmolc is a centimole of *charge*, so for a +1 cation
// 1 cmolc/dm³ = 0.01 mol × (molar mass) g = (molar mass × 10) mg/dm³. Hence
// divide mg/dm³ by 10× the molar mass: K 39.1 → 391, Na 23 → 230. Validated
// against Laudo nº 388/2026 (K 27.51 mg/dm³ = 0.07 cmolc/dm³).
const K_MG_TO_CMOLC = 1 / 391;
const NA_MG_TO_CMOLC = 1 / 230;
export const UNIT_FAMILIES = {
    phosphorus: {
        allowedUnits: PHOSPHORUS_UNITS,
        defaultUnit: "mg/dm³",
        canonicalUnit: "mg/dm³",
        // mg/dm³, mg/kg and ppm are numerically interchangeable for soil P.
        toCanonical: { "mg/dm³": 1, "mg/kg": 1, ppm: 1 },
    },
    potassium: {
        allowedUnits: POTASSIUM_UNITS,
        defaultUnit: "mg/dm³",
        canonicalUnit: "cmolc/dm³",
        toCanonical: { "mg/dm³": K_MG_TO_CMOLC, "cmolc/dm³": 1, "mmolc/dm³": 0.1 },
    },
    sodium: {
        allowedUnits: SODIUM_UNITS,
        defaultUnit: "mg/dm³",
        canonicalUnit: "cmolc/dm³",
        toCanonical: { "mg/dm³": NA_MG_TO_CMOLC, "cmolc/dm³": 1, "mmolc/dm³": 0.1 },
    },
    cation: {
        allowedUnits: CATION_UNITS,
        defaultUnit: "cmolc/dm³",
        canonicalUnit: "cmolc/dm³",
        toCanonical: { "cmolc/dm³": 1, "mmolc/dm³": 0.1 },
    },
    sulfur: {
        allowedUnits: SULFUR_UNITS,
        defaultUnit: "mg/dm³",
        canonicalUnit: "mg/dm³",
        toCanonical: { "mg/dm³": 1, "mg/kg": 1, ppm: 1 },
    },
    micronutrient: {
        allowedUnits: MICRONUTRIENT_UNITS,
        defaultUnit: "mg/dm³",
        canonicalUnit: "mg/dm³",
        toCanonical: { "mg/dm³": 1, "mg/kg": 1, ppm: 1 },
    },
    organic: {
        allowedUnits: ORGANIC_UNITS,
        defaultUnit: "g/kg",
        canonicalUnit: "g/kg",
        // 1 dag/kg = 1% = 10 g/kg; g/dm³ ≈ g/kg for soil (density ~1).
        toCanonical: { "g/kg": 1, "dag/kg": 10, "g/dm³": 1 },
    },
    texture: {
        allowedUnits: TEXTURE_UNITS,
        defaultUnit: "g/kg",
        canonicalUnit: "%",
        toCanonical: { "%": 1, "g/kg": 0.1 },
    },
    percent: {
        allowedUnits: PERCENT_UNITS,
        defaultUnit: "%",
        canonicalUnit: "%",
        toCanonical: { "%": 1 },
    },
};
/**
 * Every unit-bearing soil metric → its unit family. pH (dimensionless) and the
 * cation ratios (dimensionless) are *not* here — they carry no unit and are
 * modelled directly on `SoilAnalysisResults`. The trailing entries are
 * engine-*derived* (see `DERIVED_METRIC_KEYS`): declared so the engine can
 * format them, but never captured.
 */
export const SOIL_METRICS = {
    // Organic
    organicMatter: { family: "organic" },
    organicCarbon: { family: "organic" },
    // Phosphorus (per extraction method)
    phosphorusMehlich: { family: "phosphorus" },
    phosphorusResin: { family: "phosphorus" },
    phosphorusMehlich3: { family: "phosphorus" },
    phosphorusRemaining: { family: "phosphorus" },
    phosphorusTotal: { family: "phosphorus" },
    // Macronutrient cations
    potassium: { family: "potassium" },
    sodium: { family: "sodium" },
    calcium: { family: "cation" },
    magnesium: { family: "cation" },
    aluminum: { family: "cation" },
    hydrogenAluminum: { family: "cation" },
    sulfur: { family: "sulfur" },
    // Micronutrients
    boron: { family: "micronutrient" },
    copper: { family: "micronutrient" },
    iron: { family: "micronutrient" },
    manganese: { family: "micronutrient" },
    zinc: { family: "micronutrient" },
    // Texture
    clay: { family: "texture" },
    silt: { family: "texture" },
    sand: { family: "texture" },
    // Derived (engine output)
    sumOfBases: { family: "cation" },
    effectiveCec: { family: "cation" },
    totalCec: { family: "cation" },
    baseSaturation: { family: "percent" },
    aluminumSaturation: { family: "percent" },
};
/** Metrics the interpretation engine computes — never captured or stored. */
export const DERIVED_METRIC_KEYS = [
    "sumOfBases",
    "effectiveCec",
    "totalCec",
    "baseSaturation",
    "aluminumSaturation",
];
const DERIVED_KEY_SET = new Set(DERIVED_METRIC_KEYS);
export const CAPTURED_METRIC_KEYS = Object.keys(SOIL_METRICS).filter((k) => !DERIVED_KEY_SET.has(k));
// ============================================
// INTERPRETATION SYSTEM → DEFAULT-UNIT PRESETS
// ============================================
export const INTERPRETATION_SYSTEMS = [
    "IAC",
    "CFSEMG",
    "EMBRAPA_CERRADO",
];
/**
 * Region/system → default unit for the metrics whose customary unit varies by
 * system. Selecting a system pre-selects these in the capture form; the user
 * can still override per metric. Anything not listed falls back to the family
 * `defaultUnit`. `primaryPhosphorusMethod` is the P field a report in that
 * system usually fills (IAC → resina, CFSEMG/Embrapa → Mehlich-1).
 */
export const REGION_UNIT_PRESETS = {
    IAC: {
        cationUnit: "mmolc/dm³",
        potassiumUnit: "mmolc/dm³",
        primaryPhosphorusMethod: "phosphorusResin",
    },
    CFSEMG: {
        cationUnit: "cmolc/dm³",
        potassiumUnit: "mg/dm³",
        primaryPhosphorusMethod: "phosphorusMehlich",
    },
    EMBRAPA_CERRADO: {
        cationUnit: "cmolc/dm³",
        potassiumUnit: "mg/dm³",
        primaryPhosphorusMethod: "phosphorusMehlich",
    },
};
// ============================================
// HELPERS
// ============================================
export function getMetricFamily(metric) {
    return UNIT_FAMILIES[SOIL_METRICS[metric].family];
}
export function isUnitAllowed(metric, unit) {
    return getMetricFamily(metric).allowedUnits.includes(unit);
}
// ============================================
// EXTRACTED-UNIT NORMALISATION
// ============================================
/**
 * Fold a printed unit to a comparison key: no surrounding or internal spaces,
 * `dm3`/`dm^3` written as `dm³`, case-insensitive. A Laudo is transcribed from
 * a PDF or a photo, so the same unit arrives spelled several ways.
 */
function foldUnit(raw) {
    return raw
        .trim()
        .replace(/\s+/g, "")
        .replace(/dm\^?3/gi, "dm³")
        .toLowerCase();
}
/**
 * Printed spellings that MEAN an allowed unit but are not one, keyed by their
 * folded form.
 *
 * `cmol/dm³` is the load-bearing entry: Brazilian labs report centimoles of
 * *charge* and routinely drop the `c` subscript when printing. Read literally
 * the reading is a different quantity, so this alias is asserted against data
 * rather than assumed — on Laudo nº 388/2026, reading Ca/Mg/Al/H+Al as
 * `cmolc/dm³` reproduces the report's own printed SB, t, T, V% and m% exactly,
 * which the literal reading does not.
 *
 * A litre and a dm³ are the same volume, so `/l` spellings map across directly.
 */
const UNIT_ALIASES = {
    "cmol/dm³": "cmolc/dm³",
    "mmol/dm³": "mmolc/dm³",
    "cmol/l": "cmolc/dm³",
    "cmolc/l": "cmolc/dm³",
    "mmol/l": "mmolc/dm³",
    "mmolc/l": "mmolc/dm³",
    "mg/l": "mg/dm³",
    "g/l": "g/dm³",
};
/**
 * Resolve a unit as printed on a lab report to one this metric actually allows,
 * or `undefined` when it cannot be resolved.
 *
 * Extraction is model output over a user-supplied file: the unit arrives as free
 * text and only has to *look* like what the report printed. Everything
 * downstream — the write-path validator, the interpretation engine's
 * `toCanonical` factors — is keyed on the exact registry spelling, so an
 * unresolved unit silently drops the reading out of every computation.
 *
 * Returning `undefined` rather than falling back to the family default is
 * deliberate: a default would keep the NUMBER while changing what it means
 * (`mmolc/dm³` and `cmolc/dm³` differ by 10×). An unresolvable unit is a
 * question for the person reviewing the report, not a value to guess at.
 */
export function normalizeSoilUnit(metric, raw) {
    const { allowedUnits } = getMetricFamily(metric);
    if (allowedUnits.includes(raw))
        return raw;
    const folded = foldUnit(raw);
    const spelledDifferently = allowedUnits.find((u) => foldUnit(u) === folded);
    if (spelledDifferently)
        return spelledDifferently;
    const aliased = UNIT_ALIASES[folded];
    return aliased !== undefined && allowedUnits.includes(aliased)
        ? aliased
        : undefined;
}
/** Unit to pre-select for a metric, honouring the region/system preset. */
export function getDefaultUnit(metric, system) {
    const family = SOIL_METRICS[metric].family;
    if (system) {
        const preset = REGION_UNIT_PRESETS[system];
        if (family === "cation")
            return preset.cationUnit;
        if (family === "potassium")
            return preset.potassiumUnit;
    }
    return UNIT_FAMILIES[family].defaultUnit;
}
/**
 * Convert a reading to the metric's canonical unit. Throws on an unknown unit
 * so callers must validate against `isUnitAllowed` first (the engine and the
 * write-path validator both do).
 */
export function convertToCanonical(metric, value, unit) {
    const family = getMetricFamily(metric);
    const factor = family.toCanonical[unit];
    if (factor === undefined) {
        throw new Error(`Unit "${unit}" is not valid for metric "${metric}" (allowed: ${family.allowedUnits.join(", ")})`);
    }
    return value * factor;
}
/**
 * Convert a reading between any two units of the same metric (e.g. K 27,51 mg/dm³
 * ↔ 0,07 cmolc/dm³). Routes through the canonical unit using the family factors —
 * `value · toCanonical[from] / toCanonical[to]` — so the display layer can
 * re-express a stored `{ value, unit }` in a user-chosen unit without keeping its
 * own conversion table. Throws on a unit outside the metric's family.
 */
export function convertUnit(metric, value, fromUnit, toUnit) {
    if (fromUnit === toUnit)
        return value;
    const family = getMetricFamily(metric);
    const from = family.toCanonical[fromUnit];
    const to = family.toCanonical[toUnit];
    if (from === undefined || to === undefined) {
        throw new Error(`Cannot convert metric "${metric}" between "${fromUnit}" and "${toUnit}" (allowed: ${family.allowedUnits.join(", ")})`);
    }
    return (value * from) / to;
}
//# sourceMappingURL=SoilUnitRegistry.js.map